import React, { useMemo, useState } from 'react';
import useStyles from './Graph.style';
import { Grid, Box, Card, CardContent, FormControl, Select } from '@material-ui/core';
import { MultilevelHeaderTable } from '../../../components/CostBreakup/MultilevelHeaderTable';
import { DATA_TRANSFER_TOP10RESOURCES_TABS, Top10ResourcesTabs } from '../costBreakupTabs';
import { COST_BREAKUP_FIELD_LABELS, DATA_TRANSFER_SUMMABLE_COLUMNS_BY_TAB } from '../costBreakupTableColumns';
import Header from '../../../components/CostBreakup/Header';
import { Typography } from '@mui/material';

const TopTenResources: React.FC<{ data: Record<string, any> }> = ({ data }) => {
    const hasData = data && Object.keys(data).length > 0;
    const classes = useStyles();
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const dummyTableData = [{
        UsageAccountId: '276505133448',
        name: 'No Name',
        ResourceId: 'i-0ee24e82424b9b111',
        RegionName: 'Oregon',
        interAZQuantity: 47.98,
        interAZCost: 0.48,
        internetOutQuantity: 0,
        internetOutCost: 0,
        regionToRegionQuantity: 1887.07,
        regionToRegionCost: 37.74,
        totalcost: 38.22,
    }];
    const summaryData = {
        totalCost: 205.29,
        interAZ: { quantity: 8631.45, cost: 86.30 },
        internetOut: { quantity: 883.79, cost: 76.56 },
        regionToRegion: { quantity: 2121.30, cost: 42.43 },
    };
    const [tableData, setTableData] = useState(dummyTableData);
    const [selectedTab, setSelectedTab] = useState<Top10ResourcesTabs>(DATA_TRANSFER_TOP10RESOURCES_TABS[0]);
    const handleTabClick = (newTab: Top10ResourcesTabs) => {
        setSelectedTab(newTab);
    };
    const [sortColumn, setSortColumn] = useState<string>('');
    const columns = useMemo(() => {
    if (!tableData || tableData.length === 0) return [];

    // Collect all unique keys across all entries
    const allKeysSet = new Set<string>();
    for (const item of tableData) {
        Object.keys(item).forEach((key) => allKeysSet.add(key));
    }

    return Array.from(allKeysSet).map((key) => ({
        key,
        label: COST_BREAKUP_FIELD_LABELS.find((f) => f.key === key)?.label ?? key,
    }));
    }, [tableData]);

    const summableColumns = useMemo(() => {
    return DATA_TRANSFER_SUMMABLE_COLUMNS_BY_TAB[selectedTab] ?? [];
    }, [selectedTab]);

    const handleSort = (column: string) => {
        const newDirection =
        sortColumn === column && sortDirection === 'desc' ? 'asc' : 'desc';

        setSortColumn(column);
        setSortDirection(newDirection);
    };

    const sortedServiceEntries = useMemo(() => {
        const serviceEntries = Object.entries(tableData ?? {}).filter(
          ([key]) => key !== 'Total Cost'
        ) as [string, Record<string, any>][];
        
        if (!sortColumn) return serviceEntries;
    
        return [...serviceEntries].sort((a, b) => {
          let valueA = a[1][sortColumn];
          let valueB = b[1][sortColumn];
    
          // Null / undefined fallback
          if (valueA === undefined || valueA === null) valueA = '';
          if (valueB === undefined || valueB === null) valueB = '';
    
          // If both are numbers
          if (!isNaN(Number(valueA)) && !isNaN(Number(valueB))) {
            return sortDirection === 'asc'
              ? Number(valueA) - Number(valueB)
              : Number(valueB) - Number(valueA);
          }
    
          // Otherwise compare as strings
          const strA = String(valueA).toLowerCase();
          const strB = String(valueB).toLowerCase();
          return sortDirection === 'asc'
            ? strA.localeCompare(strB)
            : strB.localeCompare(strA);
        });
      }, [tableData, sortColumn, sortDirection]);

    return (
        <div className="card-wrapper">
            <Box className={classes.headerWrapper} display="flex" justifyContent="space-between" alignItems="center">
                <Grid item className={classes.chartTitle}>
                    Data Transfer Top 10 Resources
                </Grid>
            </Box>
            <div className="parent-container">
                <Grid item md={12} sm={12} xs={12}>
                    <Header
                        tabs={[...DATA_TRANSFER_TOP10RESOURCES_TABS]}
                        selectedTab={selectedTab}
                        onTabChange={handleTabClick}
                        showMonthFilter={false}
                        showExcelDownload={false}
                    />
                    {hasData ? (
                        <div>
                            <div className="summary-row-with-arrow">
                                <div className="summary-total">
                                    <Card className="invoiceCard no-margin-right">
                                        <CardContent className="text-white">
                                        <Typography variant="h5" component="div">
                                            Total
                                        </Typography>
                                        <Typography variant="h4" className="mt-10 text-white">
                                            <div className="invoiceMonthSelect mb-0">
                                                $ 228.7
                                            </div>
                                        </Typography>
                                        </CardContent>
                                    </Card>
                                </div>
                                <div className="summary-arrow">→</div>
                                <div className="summary-cards">
                                    <div className="summary-row">
                                        <div
                                            className="idleBox-clone inventoryBox inner-space"
                                        >
                                            <div style={{ width: '12vw', paddingLeft: '5px' }}>
                                            <div className="font-18px inventoryResource">
                                                Inter-AZ
                                            </div>
                                            <div className="pt-10">
                                                <p>Inter-AZ (GB): 10,279.35</p>
                                                <p>
                                                Cost ($):{' '}
                                                <span className="fw-700">
                                                    $86.30
                                                </span>
                                                </p>
                                            </div>
                                            </div>
                                        </div>
                                        <div
                                            className="idleBox-clone inventoryBox inner-space"
                                        >
                                            <div style={{ width: '12vw', paddingLeft: '5px' }}>
                                            <div className="font-18px inventoryResource">
                                                Internet Out
                                            </div>
                                            <div className="pt-10">
                                                <p>Inter-AZ (GB): 10,279.35</p>
                                                <p>
                                                Cost ($):{' '}
                                                <span className="fw-700">
                                                    $86.30
                                                </span>
                                                </p>
                                            </div>
                                            </div>
                                        </div>
                                        <div
                                            className="idleBox-clone inventoryBox inner-space"
                                        >
                                            <div style={{ width: '12vw', paddingLeft: '5px' }}>
                                                <div className="font-18px inventoryResource">
                                                    Region To Region
                                                </div>
                                                <div className="pt-10">
                                                    <p>Inter-AZ (GB): 10,279.35</p>
                                                    <p>
                                                    Cost ($):{' '}
                                                    <span className="fw-700">
                                                        $86.30
                                                    </span>
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="table-wrapper">
                                <MultilevelHeaderTable
                                    loading={false}
                                    sortedEntries={sortedServiceEntries}
                                    onSort={handleSort}
                                    columns={columns}
                                    summableColumns={summableColumns}
                                    sortColumn={sortColumn}            
                                    sortDirection={sortDirection}       
                                />
                            </div>
                        </div>
                    ) : (
                    <div className="no-data">
                        No data available
                    </div>
                    )}
                </Grid>
            </div>
        </div>
    );
};

export default TopTenResources;